import React, { useEffect, useState } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Box,
  Link
} from "@material-ui/core";
import { Form, Field } from "react-final-form";
import { IntroText, FormActionDrawer } from "../../../components";
import createDecorator from "final-form-focus";
import { useHeader } from "../../../contexts";
import { string, object } from "yup";
import { useYupValidation } from "../../../hooks";
import {
  COUNTRY_STATES,
  COUNTRIES,
  COUNTRIES_ISO_CODES
} from "../../../constants";
import { useTranslation } from "react-i18next";
import { MapboxLocationAutocomplete } from "../../../components/Common/MapboxLocationAutocomplete";

const focusOnErrors = createDecorator();

const validationSchema = object({
  country: string()
    .required()
    .trim()
    .label("Country"),
  street: string()
    .required()
    .trim()
    .label("Street"),
  city: string()
    .required()
    .trim()
    .label("City"),
  state: string()
    .required()
    .trim()
    .label("State"),
  zip_code: string()
    .required()
    .matches(/^[0-9]+$/, "Zip code must be only digits")
    .min(5, "Zip code must be exactly 5 digits")
    .max(5, "Zip code must be exactly 5 digits")
    .label("Zip code")
});

const SchedulePatientAddress = ({
  setDrawerClearance,
  updateScheduleData,
  scheduleData,
  isSubmitting
}) => {
  const { t } = useTranslation();
  const { setTitle } = useHeader();
  const { validate } = useYupValidation();

  const [manualEntry, setManualEntry] = useState(false);
  const [address, setAddress] = useState();

  useEffect(() => {
    setTitle(t("schedule.patient_address.title"));
  }, []);

  useEffect(() => {
    if (scheduleData.patientAddress) {
      setAddress(scheduleData.patientAddress);
    }
  }, [scheduleData]);

  const validateForm = async data => {
    const errors = await validate(validationSchema)(data);

    if (errors) return errors;

    updateScheduleData({ patientAddress: data });
  };

  const handleUpdateLocation = location => {
    if (!location) return;

    let street, city, state, zip_code, country;

    location.address
      ? (street = location.address.concat(" ", location.text))
      : (street = location.text);

    location.context.forEach(i => {
      const [context] = i.id.split(".");

      if (context === "place") {
        city = i.text;
      } else if (context === "region") {
        const [, theState] = i.short_code.split("-");
        state = theState;
      } else if (context === "postcode") {
        zip_code = i.text;
      } else if (context === "country") {
        country = i.short_code.toUpperCase();
      }
    });

    setAddress({
      street,
      city,
      state,
      zip_code,
      country
    });
  };

  const resetAddress = () => {
    setManualEntry(false);
    setAddress();
  };

  return (
    <Box data-cy="schedule-screen-patient-address">
      <IntroText>{t("schedule.patient_address.intro_text")}</IntroText>
      {!manualEntry && !address ? (
        <>
          <Box mb={1}>
            <MapboxLocationAutocomplete
              geocodeConfig={{
                types: ["address"],
                countries: [COUNTRIES_ISO_CODES.US, COUNTRIES_ISO_CODES.MX]
              }}
              onUpdateLocation={handleUpdateLocation}
              label={t("schedule.patient_address.form.search.label")}
            />
          </Box>
          <Box textAlign="right">
            <Link
              data-cy="schedule-screen-patient-address-cant-find"
              component="button"
              variant="body2"
              onClick={() => setManualEntry(!manualEntry)}
            >
              {t("schedule.patient_address.link_manual_address")}
            </Link>
          </Box>
        </>
      ) : (
        <Form
          onSubmit={validateForm}
          initialValues={{ state: "", ...(address || {}) }}
          decorators={[focusOnErrors]}
          render={({ handleSubmit, submitErrors: errors = {}, values }) => (
            <form onSubmit={handleSubmit}>
              <Field
                name="country"
                render={({ input }) => (
                  <FormControl margin="normal" fullWidth variant="outlined">
                    <InputLabel id="application-address-country">
                      {t("schedule.patient_address.form.country.label")}
                    </InputLabel>
                    <Select
                      {...input}
                      native
                      labelId="application-address-country"
                      id="application-address-country"
                      label="Country"
                      fullWidth
                      inputProps={{ "data-cy": "input-address-country" }}
                    >
                      {[{ value: "", label: "" }, ...COUNTRIES].map(country => (
                        <option
                          key={`country-${country.iso_alpha_2}`}
                          value={country.iso_alpha_2}
                        >
                          {country.label}
                        </option>
                      ))}
                    </Select>
                    {!!errors.country && (
                      <FormHelperText error={true}>
                        {errors.country}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              {values.country && (
                <>
                  <Field
                    name="street"
                    render={({ input }) => (
                      <TextField
                        {...input}
                        label={t("schedule.patient_address.form.street.label")}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        error={!!errors.street}
                        helperText={errors.street}
                        inputProps={{
                          "data-lpignore": "true",
                          "data-cy": "input-street"
                        }}
                      />
                    )}
                  />
                  <Field
                    name="city"
                    render={({ input }) => (
                      <TextField
                        {...input}
                        label={t("schedule.patient_address.form.city.label")}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        error={!!errors.city}
                        helperText={errors.city}
                        inputProps={{
                          "data-lpignore": "true",
                          "data-cy": "input-city"
                        }}
                      />
                    )}
                  />
                  <Field
                    name="state"
                    render={({ input }) => (
                      <FormControl margin="normal" fullWidth variant="outlined">
                        <InputLabel id="application-address-state">
                          {t("schedule.patient_address.form.state.label")}
                        </InputLabel>
                        <Select
                          {...input}
                          native
                          labelId="application-address-state"
                          id="application-address-state"
                          label="State"
                          fullWidth
                          inputProps={{ "data-cy": "input-address-state" }}
                        >
                          {[
                            { value: "", label: "" },
                            ...(COUNTRY_STATES[values.country] || [])
                          ].map(state => (
                            <option
                              key={`state-${state.value}`}
                              value={state.value}
                            >
                              {state.label}
                            </option>
                          ))}
                        </Select>
                        {!!errors.state && (
                          <FormHelperText error={true}>
                            {errors.state}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  <Field
                    name="zip_code"
                    render={({ input }) => (
                      <TextField
                        {...input}
                        label={t(
                          "schedule.patient_address.form.zip_code.label"
                        )}
                        fullWidth
                        margin="normal"
                        type="tel"
                        variant="outlined"
                        error={!!errors.zip_code}
                        helperText={errors.zip_code}
                        inputProps={{
                          "data-lpignore": "true",
                          "data-cy": "input-zip_code"
                        }}
                      />
                    )}
                  />
                </>
              )}
              <Box textAlign="right">
                <Link
                  type="button"
                  component="button"
                  variant="body2"
                  onClick={() => resetAddress()}
                >
                  {t("schedule.patient_address.link_address_search")}
                </Link>
              </Box>
              <FormActionDrawer
                setDrawerClearance={setDrawerClearance}
                loading={isSubmitting}
              />
            </form>
          )}
        />
      )}
    </Box>
  );
};

export { SchedulePatientAddress };
